import { useState, useEffect, useCallback, useRef } from 'react';

interface UseInactivityLogoutProps {
  onLogout: () => void;
  inactivityTime?: number; // in milliseconds, default 30 minutes
  warningTime?: number; // in milliseconds, default 90 seconds
  enabled?: boolean; // whether to enable the inactivity timer
}

export const useInactivityLogout = ({
  onLogout,
  inactivityTime = 30 * 60 * 1000, // 30 minutes
  warningTime = 90 * 1000, // 90 seconds
  enabled = true, // enabled by default
}: UseInactivityLogoutProps) => {
  const [showWarning, setShowWarning] = useState(false);
  const [countdown, setCountdown] = useState(0);

  const inactivityTimer = useRef<NodeJS.Timeout | null>(null);
  const warningTimer = useRef<NodeJS.Timeout | null>(null);
  const countdownTimer = useRef<NodeJS.Timeout | null>(null);

  const clearAllTimers = useCallback(() => {
    if (inactivityTimer.current) {
      clearTimeout(inactivityTimer.current);
      inactivityTimer.current = null;
    }
    if (warningTimer.current) {
      clearTimeout(warningTimer.current);
      warningTimer.current = null;
    }
    if (countdownTimer.current) {
      clearInterval(countdownTimer.current);
      countdownTimer.current = null;
    }
  }, []);

  const startCountdown = useCallback(() => {
    let seconds = Math.floor(warningTime / 1000);
    setCountdown(seconds);

    countdownTimer.current = setInterval(() => {
      seconds -= 1;
      setCountdown(seconds);

      if (seconds <= 0) {
        clearAllTimers();
        onLogout();
      }
    }, 1000);
  }, [warningTime, onLogout, clearAllTimers]);

  const showInactivityWarning = useCallback(() => {
    setShowWarning(true);
    startCountdown();

    // Auto logout after warning time if no interaction
    warningTimer.current = setTimeout(() => {
      clearAllTimers();
      onLogout();
    }, warningTime);
  }, [startCountdown, warningTime, onLogout, clearAllTimers]);

  const resetTimer = useCallback(() => {
    clearAllTimers();
    setShowWarning(false);
    setCountdown(0);

    // Start new inactivity timer
    inactivityTimer.current = setTimeout(() => {
      showInactivityWarning();
    }, inactivityTime);
  }, [inactivityTime, showInactivityWarning, clearAllTimers]);

  const handleUserActivity = useCallback(() => {
    if (showWarning) {
      // If warning is showing, dismiss it and reset
      setShowWarning(false);
      setCountdown(0);
    }
    resetTimer();
  }, [showWarning, resetTimer]);

  const dismissWarning = useCallback(() => {
    // Manually dismiss warning and reset timer
    setShowWarning(false);
    setCountdown(0);
    resetTimer();
  }, [resetTimer]);

  useEffect(() => {
    // Only run if enabled (user is logged in)
    if (!enabled) {
      // Clear any existing timers and return early
      clearAllTimers();
      setShowWarning(false);
      setCountdown(0);
      return;
    }

    // List of events to track for user activity
    const events = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click',
      'keydown'
    ];

    let isInitialized = false;

    const throttledHandler = (() => {
      let lastCall = 0;
      return (event: Event) => {
        const now = Date.now();
        if (now - lastCall >= 5000) { // Throttle to once per 5 seconds to reduce excessive resets
          lastCall = now;
          // Always reset timer on activity (this will dismiss warning if showing)
          resetTimer();
        }
      };
    })();

    // Add event listeners
    events.forEach(event => {
      document.addEventListener(event, throttledHandler, true);
    });

    // Start initial timer with delay to prevent immediate popup
    const initTimer = setTimeout(() => {
      if (!isInitialized) {
        isInitialized = true;
        resetTimer();
      }
    }, 2000); // 2 second delay after component mount

    return () => {
      // Cleanup
      clearTimeout(initTimer);
      events.forEach(event => {
        document.removeEventListener(event, throttledHandler, true);
      });
      clearAllTimers();
    };
  }, [resetTimer, clearAllTimers, enabled]); // Include necessary dependencies

  return {
    showWarning,
    countdown,
    dismissWarning,
  };
};